import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { ArrowLeft, Send, AlertCircle, CheckCircle2 } from "lucide-react";
import {
    getFeedbackFormByTitle,
    submitFeedbackResponse,
} from "../../api/feedbackService";
import { useAuth } from "../../hooks/useAuth";
import { FeedbackResponse, FeedbackForm } from "./types";

import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
    CardFooter,
} from "../../components/ui/Card";
import { RadioGroup, RadioGroupItem } from "../../components/ui/RadioGroup";
import { Label } from "../../components/ui/Label";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/Alert";
import usePartnerAwareNavigate from "src/hooks/usePartnerAwareNavigate";

const FeedbackPage = () => {
    const { title } = useParams<{ title: string }>();
    const navigate = usePartnerAwareNavigate();
    const { user, loading: authLoading } = useAuth();
    const [responses, setResponses] = useState<
        Record<string, string | string[]>
    >({});
    const [status, setStatus] = useState<{
        type: "success" | "error";
        message: string;
    } | null>(null);
    const [validationErrors, setValidationErrors] = useState<
        Array<{ questionId: string; message: string }>
    >([]);

    const { data: form, isLoading: formLoading } = useQuery({
        queryKey: ["feedbackForm", title],
        queryFn: () => getFeedbackFormByTitle(title || ""),
        enabled: !!title,
    });

    const submitMutation = useMutation({
        mutationFn: (responseData: { responses: FeedbackResponse[] }) =>
            submitFeedbackResponse(form?.id || "", user?.uid || "", {
                ...responseData,
                submittedAt: new Date(),
            }),
        onSuccess: () => {
            setStatus({
                type: "success",
                message: "Thank you for your feedback!",
            });
            setTimeout(() => navigate("/"), 1500);
        },
        onError: (error: Error) => {
            setStatus({
                type: "error",
                message: error.message,
            });
        },
    });

    const handleInputChange = (
        questionId: string,
        value: string | string[],
        isMultiple = false
    ) => {
        setResponses((prev) => {
            const newValue = isMultiple
                ? Array.isArray(prev[questionId])
                    ? (prev[questionId] as string[]).includes(value as string)
                        ? (prev[questionId] as string[]).filter(
                              (v) => v !== value
                          )
                        : [...(prev[questionId] as string[]), value as string]
                    : [value as string]
                : value;
            console.log("Setting response:", { questionId, value: newValue }); // Debug log
            return {
                ...prev,
                [questionId]: newValue,
            };
        });
    };

    const handleSubmit = (
        e:
            | React.FormEvent<HTMLFormElement>
            | React.MouseEvent<HTMLButtonElement>
    ) => {
        e.preventDefault();

        if (!user?.uid) {
            setStatus({
                type: "error",
                message: "You must be logged in to submit feedback",
            });
            return;
        }

        if (!form) {
            setStatus({
                type: "error",
                message: "Form not found",
            });
            return;
        }

        // Validate required fields
        const errors = form.questions
            .filter((q) => {
                if (!q.isRequired) return false;
                const response = responses[q.id];
                return !response;
            })
            .map((q) => ({
                questionId: q.id,
                message: `"${q.question}" is required`,
            }));

        if (errors.length > 0) {
            setValidationErrors(errors);
            setStatus({
                type: "error",
                message: "Please fill in all required fields",
            });
            return;
        }

        setValidationErrors([]);

        // Format responses with question text for better context
        const formattedResponses = Object.entries(responses).map(
            ([questionId, value]) => {
                const question = form.questions.find(
                    (q) => q.id === questionId
                );
                if (!question) return { questionId, value };

                // For multiple choice, we already have the text value
                return {
                    questionId,
                    value,
                    questionText: question.question,
                    type: question.type,
                };
            }
        );

        submitMutation.mutate({
            responses: formattedResponses,
        });

        navigate("/");
        window.location.reload();
    };

    const renderQuestion = (question: FeedbackForm["questions"][0]) => {
        switch (question.type) {
            case "text":
                return (
                    <Input
                        id={`question-${question.id}`}
                        value={(responses[question.id] as string) || ""}
                        placeholder="Your answer"
                        onChange={(e) =>
                            handleInputChange(question.id, e.target.value)
                        }
                    />
                );
            case "multiple_choice":
                return (
                    <RadioGroup
                        value={responses[question.id] as string}
                        onValueChange={(value) =>
                            handleInputChange(question.id, value)
                        }
                        className="space-y-2">
                        {question.options?.map((option) => (
                            <div
                                key={option.id}
                                className="flex items-center space-x-2">
                                <RadioGroupItem
                                    value={option.text}
                                    id={`option-${option.id}`}
                                />
                                <Label
                                    htmlFor={`option-${option.id}`}
                                    className="cursor-pointer">
                                    {option.text}
                                </Label>
                            </div>
                        ))}
                    </RadioGroup>
                );
            case "rating":
            case "scale":
                const maxValue = question.type === "rating" ? 5 : 5;
                return (
                    <div className="flex gap-2 flex-wrap">
                        {[...Array(maxValue)].map((_, i) => {
                            const value = (i + 1).toString();
                            const isSelected = responses[question.id] === value;
                            return (
                                <Button
                                    key={i}
                                    type="button"
                                    variant={isSelected ? "default" : "outline"}
                                    onClick={() =>
                                        handleInputChange(question.id, value)
                                    }
                                    className={`w-12 h-12 ${
                                        isSelected
                                            ? "bg-blue-500 text-white hover:bg-blue-600"
                                            : "hover:bg-gray-100"
                                    }`}>
                                    {value}
                                </Button>
                            );
                        })}
                    </div>
                );
            default:
                return null;
        }
    };

    if (authLoading || formLoading) {
        return (
            <div className="container mx-auto py-8">
                <Card>
                    <CardContent className="py-8">
                        <div className="text-center">Loading...</div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="container mx-auto py-8">
                <Card>
                    <CardContent className="py-8">
                        <div className="text-center text-red-500">
                            Please log in to submit feedback
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (!form) {
        return (
            <div className="container mx-auto py-8">
                <Card>
                    <CardContent className="py-8">
                        <div className="text-center text-red-500">
                            Form not found
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="container mx-auto py-8">
            <Card>
                <CardHeader>
                    <div className="flex items-center space-x-4">
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate("/")}>
                            <ArrowLeft className="h-4 w-4" />
                        </Button>
                        <div>
                            <CardTitle>{form.title}</CardTitle>
                            <CardDescription>
                                {form.description}
                            </CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    {status && (
                        <Alert
                            variant={
                                status.type === "error"
                                    ? "destructive"
                                    : "default"
                            }
                            className="mb-6">
                            {status.type === "error" ? (
                                <AlertCircle className="h-4 w-4" />
                            ) : (
                                <CheckCircle2 className="h-4 w-4" />
                            )}
                            <AlertTitle>
                                {status.type === "error" ? "Error" : "Success"}
                            </AlertTitle>
                            <AlertDescription>
                                {status.message}
                            </AlertDescription>
                        </Alert>
                    )}
                    <form onSubmit={handleSubmit} className="space-y-8">
                        {form.questions.map((question) => (
                            <div key={question.id} className="space-y-4">
                                <Label
                                    htmlFor={`question-${question.id}`}
                                    className="text-base font-medium">
                                    {question.question}
                                    {question.isRequired && (
                                        <span className="text-red-500 ml-1">
                                            *
                                        </span>
                                    )}
                                </Label>
                                {renderQuestion(question)}
                                {validationErrors.find(
                                    (e) => e.questionId === question.id
                                ) && (
                                    <p className="text-sm text-red-500 mt-1">
                                        {
                                            validationErrors.find(
                                                (e) =>
                                                    e.questionId === question.id
                                            )?.message
                                        }
                                    </p>
                                )}
                            </div>
                        ))}
                    </form>
                </CardContent>
                <CardFooter className="flex justify-end space-x-4">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => navigate("/")}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={submitMutation.isLoading}>
                        <Send className="h-4 w-4 mr-2" />
                        Submit Feedback
                    </Button>
                </CardFooter>
            </Card>
        </div>
    );
};

export default FeedbackPage;
